import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  createRoosterregels,
  createStandaardLesrooster,
  deleteRoosterregel,
  fetchRooster,
  restoreRoosterregel,
  saveLesrooster,
  toggleRoosterException,
  updateRoosterregel,
} from "#/modules/les.planning/api.rooster.server";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { translatedApiError } from "#/modules/util.errors/translate";

const weekdag = z.number().int().min(1).max(5);

const regelSchema = z.object({
  klasIds: z.array(z.string().min(1)).min(1),
  weekdag,
  lesuurId: z.string().min(1),
  geldigVanaf: z.string().min(1),
  geldigTot: z.string().nullable(),
  schooldocentId: z.string().min(1).nullable(),
});

const createSchema = z.object({ regels: z.array(regelSchema).min(1) });
export type CreateRoosterregelsInput = z.infer<typeof createSchema>;

const updateSchema = z.object({ id: z.string(), regel: regelSchema });
export type UpdateRoosterregelInput = z.infer<typeof updateSchema>;

const idSchema = z.object({ id: z.string() });
export type RoosterregelIdInput = z.infer<typeof idSchema>;

const exceptionSchema = z.object({ roosterregelId: z.string(), date: z.string() });
export type ToggleRoosterExceptionInput = z.infer<typeof exceptionSchema>;

const minutes = z
  .number()
  .int()
  .min(0)
  .max(24 * 60);

const lesroosterSchema = z.object({
  weekdag,
  lesduurMinuten: z.number().int().min(15).max(480),
  lesuren: z.array(
    z.object({
      id: z.string().min(1),
      startMinuten: minutes,
      duurMinuten: z.number().int().min(15).max(480),
    }),
  ),
  pauzes: z.array(
    z.object({
      id: z.string().min(1),
      naam: z.string().trim().min(1).max(50),
      startMinuten: minutes,
      duurMinuten: z.number().int().min(5).max(480),
    }),
  ),
});
export type LesroosterInput = z.infer<typeof lesroosterSchema>;

export const getRooster = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    let result = await fetchRooster();
    if (Result.isError(result)) throw result.error;
    // A school starting out gets the standaard lesuren and pauzes. Only then is it asked for, so a
    // normal read stays one call.
    if (!result.value.lesrooster) {
      const created = await createStandaardLesrooster();
      if (Result.isOk(created) && created.value.lesroosterCreated) {
        result = await fetchRooster();
        if (Result.isError(result)) throw result.error;
      }
    }
    return {
      lesrooster: result.value.lesrooster ?? null,
      regels: result.value.regels,
      docenten: result.value.docenten,
    };
  });

export const saveLesroosterFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: LesroosterInput) => lesroosterSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await saveLesrooster(data);
    if (Result.isError(result)) throw translatedApiError(result.error);
  });

export const createRoosterregelsFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: CreateRoosterregelsInput) => createSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await createRoosterregels(data);
    if (Result.isError(result)) throw translatedApiError(result.error);
    return result.value;
  });

export const updateRoosterregelFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: UpdateRoosterregelInput) => updateSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await updateRoosterregel(data);
    if (Result.isError(result)) throw translatedApiError(result.error);
    return result.value;
  });

export const deleteRoosterregelFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: RoosterregelIdInput) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await deleteRoosterregel(data.id);
    if (Result.isError(result)) throw translatedApiError(result.error);
    return result.value;
  });

export const restoreRoosterregelFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: RoosterregelIdInput) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await restoreRoosterregel(data.id);
    if (Result.isError(result)) throw translatedApiError(result.error);
    return result.value;
  });

export const toggleRoosterExceptionFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: ToggleRoosterExceptionInput) => exceptionSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await toggleRoosterException({
      roosterregelId: data.roosterregelId,
      datum: data.date,
    });
    if (Result.isError(result)) throw translatedApiError(result.error);
    return result.value;
  });
